import { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import AuthService from '../../../../services/AuthService';
import ProfessionalService from '../../../../services/ProfessionalService';
import ClientEOIService from '../../../../services/ClientEOIService';

const useChangeProfessionalModal = ({
                                        show,
                                        clientEOIId,
                                        currentProfessionalId,
                                        defaultCity,
                                        defaultService,
                                        onSuccess,
                                        onClose
                                    }) => {
    const [professionals, setProfessionals] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedProfessionalId, setSelectedProfessionalId] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (show) {
            setSearchTerm('');
            setSelectedProfessionalId(null);
            fetchProfessionals();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const fetchProfessionals = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await ProfessionalService.getAllAcceptedAndAvailableProfessionals();
            setProfessionals(data || []);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement des professionnels');
            console.error('Error fetching professionals:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Normalisation robuste ────────────────────────────────────────────
    // .normalize('NFC') recompose les accents qui peuvent être encodés
    // différemment selon la source des données, insensible à la casse et
    // aux espaces multiples.
    const normalize = (str) =>
        (str || '')
            .toString()
            .normalize('NFC')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, ' ');

    // ── Comparaisons directes champ-à-champ ─────────────────────────────
    // defaultCity/defaultService proviennent désormais de
    // eoi.professional.city / eoi.professional.selectedService, donc du
    // même format brut que pro.city / pro.selectedService : une simple
    // égalité normalisée suffit, sans découpage ville/pays.
    const matchesLocation = (proCityRaw, defaultCityRaw) => {
        if (!defaultCityRaw) return true;
        if (!proCityRaw) return false;
        return normalize(proCityRaw) === normalize(defaultCityRaw);
    };

    const matchesServiceValue = (proServiceRaw, defaultServiceRaw) => {
        if (!defaultServiceRaw) return true;
        if (!proServiceRaw) return false;
        return normalize(proServiceRaw) === normalize(defaultServiceRaw);
    };

    const filteredProfessionals = useMemo(() => {
        return professionals.filter(pro => {
            if (currentProfessionalId && String(pro.id) === String(currentProfessionalId)) {
                return false;
            }

            if (!matchesLocation(pro.city, defaultCity)) return false;
            if (!matchesServiceValue(pro.selectedService, defaultService)) return false;

            if (!searchTerm) return true;

            const term = normalize(searchTerm);
            const fullName = normalize(`${pro.user?.firstName || ''} ${pro.user?.lastName || ''}`);
            const useRef = normalize(pro.useRef || pro.user?.useRef || '');

            return fullName.includes(term) || useRef.includes(term);
        });
    }, [professionals, searchTerm, currentProfessionalId, defaultCity, defaultService]);

    // Pour distinguer "personne ne correspond" de "le seul qui correspond
    // est déjà le prestataire actuel"
    const matchingProfessionalsIncludingCurrent = useMemo(() => {
        return professionals.filter(pro => {
            if (!matchesLocation(pro.city, defaultCity)) return false;
            if (!matchesServiceValue(pro.selectedService, defaultService)) return false;

            if (!searchTerm) return true;
            const term = normalize(searchTerm);
            const fullName = normalize(`${pro.user?.firstName || ''} ${pro.user?.lastName || ''}`);
            const useRef = normalize(pro.useRef || pro.user?.useRef || '');
            return fullName.includes(term) || useRef.includes(term);
        });
    }, [professionals, searchTerm, defaultCity, defaultService]);

    const onlyCurrentProfessionalMatches =
        matchingProfessionalsIncludingCurrent.length > 0 &&
        filteredProfessionals.length === 0 &&
        matchingProfessionalsIncludingCurrent.every(
            pro => String(pro.id) === String(currentProfessionalId)
        );

    const handleSelectProfessional = (professionalId) => {
        setSelectedProfessionalId(professionalId);
    };

    const handleConfirm = async () => {
        if (!selectedProfessionalId || !clientEOIId) return;

        const selectedPro = professionals.find(p => String(p.id) === String(selectedProfessionalId));

        const result = await Swal.fire({
            title: 'Confirmer le changement',
            html: `
                <div class="text-start">
                    <p>Voulez-vous vraiment remplacer le prestataire actuel par :</p>
                    <div class="mt-2">
                        <strong>${selectedPro?.user?.firstName || ''} ${selectedPro?.user?.lastName || ''}</strong>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, changer',
            cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const currentUser = AuthService.getCurrentUser();
            const updatedEOI = await ClientEOIService.updateProfessionalId(
                clientEOIId,
                selectedProfessionalId,
                currentUser?.username
            );

            Swal.fire({
                title: 'Prestataire changé !',
                text: 'Le nouveau prestataire a été assigné avec succès.',
                icon: 'success',
                confirmButtonColor: '#3085d6'
            });

            if (onSuccess) onSuccess(updatedEOI);
            if (onClose) onClose();
        } catch (err) {
            console.error('Error updating professional:', err);
            Swal.fire('Erreur !', 'Une erreur est survenue lors du changement de prestataire.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getProfessionalProfilePicture = (professional) => {
        if (!professional) return null;
        return (
            professional.profilePhotoUrlPresigned ||
            professional.profilePhotoUrl ||
            professional.user?.profilePictureUrl ||
            null
        );
    };

    return {
        filteredProfessionals,
        onlyCurrentProfessionalMatches,
        isLoading,
        error,
        isSubmitting,
        selectedProfessionalId,
        searchTerm,
        setSearchTerm,
        handleSelectProfessional,
        handleConfirm,
        getProfessionalProfilePicture
    };
};

export default useChangeProfessionalModal;